// src/mobile_pages/system/User_company.jsx
import React, { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  listCompanies,
  updateCompany,
  deleteCompany,
  listFactories,
} from "../../api/users/companies";
import MobileSidePanel from "../../components/common/mobile/MobileSidePanel";
import MobileFactoryModal from "../../components/company/MobileFactoryModal";
import ConfirmDialog from "../../components/common/ui/ConfirmModal";
import { Building2, Phone, Mail, MapPin, Search, Plus, ChevronRight, Factory } from "lucide-react";

const EMPTY_COMPANY = {
  name: "",
  registration_no: "",
  address: "",
  detail_address: "",
  extra_address: "",
  phone: "",
  email: "",
};

const User_company = () => {
  const navigate = useNavigate();
  const projectId = localStorage.getItem("projectId");

  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");

  // 회사 상세/수정 패널
  const [panelOpen, setPanelOpen] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [editForm, setEditForm] = useState({ ...EMPTY_COMPANY });
  const [savingEdit, setSavingEdit] = useState(false);

  // 공장 관리 모달
  const [factoryModalOpen, setFactoryModalOpen] = useState(false);
  const [factoryCompany, setFactoryCompany] = useState(null);
  const [factoryList, setFactoryList] = useState([]);

  // 삭제 확인
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deleteTargetId, setDeleteTargetId] = useState(null);

  useEffect(() => {
    loadCompanies();
  }, []);

  const loadCompanies = async (query = "") => {
    setLoading(true);
    try {
      const res = await listCompanies({ search: query });
      const data = res?.data ?? res;
      const rows = Array.isArray(data) ? data : data?.results ?? [];
      setCompanies(rows);
    } catch (error) {
      console.error("회사 목록 불러오기 실패:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async (e) => {
    e?.preventDefault();
    await loadCompanies(search);
  };

  // 상세/수정 패널 열기
  const openViewPanel = (company) => {
    setSelectedCompany(company);
    setEditForm({ ...company });
    setIsEditMode(false);
    setPanelOpen(true);
  };

  const closePanel = () => {
    setPanelOpen(false);
    setSelectedCompany(null);
    setIsEditMode(false);
  };

  // 수정 저장
  const handleSaveEdit = async () => {
    if (!selectedCompany) return;
    setSavingEdit(true);
    try {
      await updateCompany(selectedCompany.id, editForm);
      alert("회사 정보가 수정되었습니다.");
      await loadCompanies(search);
      closePanel();
    } catch (error) {
      console.error("회사 수정 실패:", error);
      alert("수정 실패\n" + JSON.stringify(error?.response?.data || {}, null, 2));
    } finally {
      setSavingEdit(false);
    }
  };

  // 삭제
  const handleDeleteCompany = async () => {
    if (!deleteTargetId) return;
    try {
      await deleteCompany(deleteTargetId);
      alert("회사가 삭제되었습니다.");
      await loadCompanies(search);
      closePanel();
    } catch (error) {
      console.error("회사 삭제 실패:", error);
      alert("삭제 실패\n" + JSON.stringify(error?.response?.data || {}, null, 2));
    } finally {
      setConfirmOpen(false);
      setDeleteTargetId(null);
    }
  };

  // 공장 모달 열기
  const openFactoryModal = async (company) => {
    setFactoryCompany({
      id: company.id,
      name: company.name,
      address: company.address,
      extra_address: company.extra_address,
      detail_address: company.detail_address,
    });
    setFactoryModalOpen(true);
    try {
      const res = await listFactories({ company: company.id });
      const data = res?.data ?? res;
      const rows = Array.isArray(data) ? data : data?.results ?? [];
      setFactoryList(rows);
    } catch (e) {
      console.error("공장 목록 불러오기 실패:", e);
      setFactoryList([]);
    }
  };

  const closeFactoryModal = () => {
    setFactoryModalOpen(false);
    setFactoryCompany(null);
    setFactoryList([]);
  };

  // Skeleton Card
  const SkeletonCard = () => (
    <div className="bg-white rounded-lg p-3 border animate-pulse">
      <div className="h-4 bg-gray-200 rounded w-1/2 mb-2"></div>
      <div className="h-3 bg-gray-200 rounded w-3/4 mb-1"></div>
      <div className="h-3 bg-gray-200 rounded w-1/2"></div>
    </div>
  );

  return (
    <div className="mobile-page-root">
      <div className="mobile-page-root-inner space-y-3 mobile-bottom-spacing">
        {/* Header */}
        <div className="bg-white rounded-lg p-3 border">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-base font-bold text-gray-900">회사 관리</h2>
              <p className="text-[10px] text-gray-500 mt-0.5">전체 {companies.length}개</p>
            </div>
            <button
              onClick={() => navigate(`/project/${projectId}/system/company/add`)}
              className="btn-create-sm flex items-center gap-1 text-xs"
            >
              <Plus className="w-3 h-3" />
              등록
            </button>
          </div>
        </div>

        {/* Search */}
        <form onSubmit={handleSearch} className="bg-white rounded-lg p-3 border">
          <div className="flex items-center gap-2">
            <div className="flex-1 relative">
              <input
                type="text"
                placeholder="회사명, 사업자번호 검색"
                className="w-full bg-gray-50 border border-gray-200 rounded-md pl-8 pr-3 py-2 text-xs focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
              <Search className="w-3.5 h-3.5 text-gray-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
            </div>
            <button type="submit" className="btn-search text-xs px-3 py-2">
              검색
            </button>
          </div>
        </form>

        {/* Company List */}
        <div className="space-y-2">
          {loading ? (
            <>
              <SkeletonCard />
              <SkeletonCard />
              <SkeletonCard />
            </>
          ) : companies.length === 0 ? (
            <div className="bg-white rounded-lg p-8 text-center border">
              <Building2 className="w-12 h-12 mx-auto text-gray-300 mb-3" />
              <p className="text-sm text-gray-500">등록된 회사가 없습니다.</p>
              <button
                onClick={() => navigate(`/project/${projectId}/system/company/add`)}
                className="btn-create mt-4"
              >
                회사 등록
              </button>
            </div>
          ) : (
            companies.map((company) => (
              <div
                key={company.id}
                className="bg-white rounded-lg p-3 border overflow-hidden"
              >
                <button
                  onClick={() => openViewPanel(company)}
                  className="w-full text-left"
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0 overflow-hidden">
                      <h3 className="text-sm font-semibold text-gray-900 truncate">
                        {company.name}
                      </h3>

                      <div className="space-y-0.5 mt-1">
                        {company.registration_no && (
                          <p className="text-[10px] text-gray-400 truncate">
                            사업자번호: {company.registration_no}
                          </p>
                        )}

                        {company.phone && (
                          <div className="flex items-center gap-1">
                            <Phone className="w-3 h-3 text-gray-400 flex-shrink-0" />
                            <span className="text-xs text-gray-600 truncate">
                              {company.phone}
                            </span>
                          </div>
                        )}

                        {company.address && (
                          <div className="flex items-center gap-1">
                            <MapPin className="w-3 h-3 text-gray-400 flex-shrink-0" />
                            <span className="text-xs text-gray-600 truncate">
                              {company.address}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>

                    <ChevronRight className="w-4 h-4 text-gray-400 flex-shrink-0" />
                  </div>
                </button>

                {/* Action Buttons */}
                <div className="flex gap-2 mt-2 pt-2 border-t">
                  <button
                    onClick={() => openFactoryModal(company)}
                    className="flex-1 flex items-center justify-center gap-1 text-xs py-1.5 rounded border border-gray-200 text-gray-600 hover:bg-gray-50"
                  >
                    <Factory className="w-3 h-3" />
                    공장관리
                  </button>
                  <button
                    onClick={() => {
                      setSelectedCompany(company);
                      setEditForm({ ...company });
                      setIsEditMode(true);
                      setPanelOpen(true);
                    }}
                    className="btn-edit-sm text-xs flex-1"
                  >
                    수정
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Company Detail/Edit Panel */}
      <MobileSidePanel
        isOpen={panelOpen}
        onClose={closePanel}
        title={
          selectedCompany
            ? isEditMode
              ? `회사 수정 – ${selectedCompany.name}`
              : `회사 상세 – ${selectedCompany.name}`
            : "회사 정보"
        }
      >
        {selectedCompany && (
          <div className="p-4 space-y-4">
            {isEditMode ? (
              // Edit Form
              <div className="space-y-3">
                <div>
                  <label className="text-xs text-gray-500 mb-1 block">회사명 *</label>
                  <input
                    className="w-full border rounded-md px-3 py-2 text-sm"
                    value={editForm.name}
                    onChange={(e) => setEditForm((v) => ({ ...v, name: e.target.value }))}
                    placeholder="회사명"
                  />
                </div>

                <div>
                  <label className="text-xs text-gray-500 mb-1 block">사업자등록번호</label>
                  <input
                    className="w-full border rounded-md px-3 py-2 text-sm"
                    value={editForm.registration_no}
                    onChange={(e) => setEditForm((v) => ({ ...v, registration_no: e.target.value }))}
                    placeholder="숫자/하이픈 포함"
                  />
                </div>

                <div>
                  <label className="text-xs text-gray-500 mb-1 block">전화번호</label>
                  <input
                    className="w-full border rounded-md px-3 py-2 text-sm"
                    value={editForm.phone}
                    onChange={(e) => setEditForm((v) => ({ ...v, phone: e.target.value }))}
                    placeholder="예: 02-1234-5678"
                  />
                </div>

                <div>
                  <label className="text-xs text-gray-500 mb-1 block">이메일</label>
                  <input
                    className="w-full border rounded-md px-3 py-2 text-sm"
                    value={editForm.email}
                    onChange={(e) => setEditForm((v) => ({ ...v, email: e.target.value }))}
                    placeholder="info@example.com"
                    type="email"
                  />
                </div>

                <div>
                  <label className="text-xs text-gray-500 mb-1 block">주소</label>
                  <input
                    className="w-full border rounded-md px-3 py-2 text-sm"
                    value={editForm.address}
                    onChange={(e) => setEditForm((v) => ({ ...v, address: e.target.value }))}
                    placeholder="도로명 주소"
                  />
                </div>

                <div>
                  <label className="text-xs text-gray-500 mb-1 block">상세주소</label>
                  <input
                    className="w-full border rounded-md px-3 py-2 text-sm"
                    value={editForm.detail_address}
                    onChange={(e) => setEditForm((v) => ({ ...v, detail_address: e.target.value }))}
                    placeholder="층/호 등"
                  />
                </div>

                <div>
                  <label className="text-xs text-gray-500 mb-1 block">참고주소</label>
                  <input
                    className="w-full border rounded-md px-3 py-2 text-sm"
                    value={editForm.extra_address}
                    onChange={(e) => setEditForm((v) => ({ ...v, extra_address: e.target.value }))}
                    placeholder="(동/리 등)"
                  />
                </div>

                {/* Edit Action Buttons */}
                <div className="flex gap-2 pt-4">
                  <button
                    className="btn-cancel flex-1"
                    onClick={() => setIsEditMode(false)}
                    disabled={savingEdit}
                  >
                    취소
                  </button>
                  <button
                    className="btn-save flex-1"
                    onClick={handleSaveEdit}
                    disabled={savingEdit}
                  >
                    {savingEdit ? "저장 중..." : "저장"}
                  </button>
                </div>
              </div>
            ) : (
              // View Mode
              <div className="space-y-3">
                <div className="section-card p-3 space-y-2">
                  <div className="flex items-center justify-between py-1 border-b">
                    <span className="text-xs text-gray-500">회사명</span>
                    <span className="text-sm font-medium">{selectedCompany.name}</span>
                  </div>
                  <div className="flex items-center justify-between py-1 border-b">
                    <span className="text-xs text-gray-500">사업자번호</span>
                    <span className="text-sm">{selectedCompany.registration_no || "-"}</span>
                  </div>
                  <div className="flex items-center justify-between py-1 border-b">
                    <span className="text-xs text-gray-500">전화번호</span>
                    <span className="text-sm">{selectedCompany.phone || "-"}</span>
                  </div>
                  <div className="flex items-center justify-between py-1 border-b">
                    <span className="text-xs text-gray-500">이메일</span>
                    <span className="text-sm">{selectedCompany.email || "-"}</span>
                  </div>
                  <div className="flex items-center justify-between py-1 border-b">
                    <span className="text-xs text-gray-500">주소</span>
                    <span className="text-sm text-right flex-1 ml-4">{selectedCompany.address || "-"}</span>
                  </div>
                  <div className="flex items-center justify-between py-1 border-b">
                    <span className="text-xs text-gray-500">상세주소</span>
                    <span className="text-sm">{selectedCompany.detail_address || "-"}</span>
                  </div>
                  <div className="flex items-center justify-between py-1">
                    <span className="text-xs text-gray-500">참고주소</span>
                    <span className="text-sm">{selectedCompany.extra_address || "-"}</span>
                  </div>
                </div>

                {/* View Action Buttons */}
                <div className="flex gap-2 pt-4">
                  <button className="btn-cancel flex-1" onClick={closePanel}>
                    닫기
                  </button>
                  <button
                    className="btn-edit flex-1"
                    onClick={() => setIsEditMode(true)}
                  >
                    수정
                  </button>
                  <button
                    className="btn-delete flex-1"
                    onClick={() => {
                      setDeleteTargetId(selectedCompany.id);
                      setConfirmOpen(true);
                    }}
                  >
                    삭제
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </MobileSidePanel>

      {/* Factory Modal */}
      {factoryModalOpen && (
        <MobileFactoryModal
          company={factoryCompany}
          factoryList={factoryList}
          onClose={closeFactoryModal}
          reloadFactoryList={async () => {
            try {
              const res = await listFactories({ company: factoryCompany.id });
              const data = res?.data ?? res;
              setFactoryList(Array.isArray(data) ? data : data?.results ?? []);
            } catch (e) {
              console.error("공장 목록 새로고침 실패:", e);
            }
          }}
        />
      )}

      {/* Delete Confirm Dialog */}
      <ConfirmDialog
        open={confirmOpen}
        title="회사 삭제"
        description="이 회사를 정말 삭제하시겠습니까? 이 작업은 되돌릴 수 없습니다."
        confirmText="삭제"
        cancelText="취소"
        onConfirm={handleDeleteCompany}
        onCancel={() => {
          setConfirmOpen(false);
          setDeleteTargetId(null);
        }}
        onClose={() => {
          setConfirmOpen(false);
          setDeleteTargetId(null);
        }}
      />
    </div>
  );
};

export default User_company;
